import { quantityAllowsFraction } from '@shared/quantity';
import {
  StockInsufficientError,
  StockProductNotFoundError,
  StockReceiveValidationError,
} from '../../domain/stockErrors';
import { reasonAllowsNegativeStock } from '../../domain/stockReasons';
import { logBusinessAuditEvent } from '../../../../shell/audit/auditLogs';
import { captureOpsFailure } from '../../../../shared/sentry/sentryOps';
import { formatSyncErrorForUser } from '../../../../sync/core/syncErrorPresentation';
import {
  planFefoAllocations,
  applyFefoAllocations,
  insertStockLot,
} from '../guards/fefoAllocator';
import {
  assertNoParentStockOnVariantProduct,
  assertProductIsInventoriable,
} from '../guards/stockInventoryGuards';
import { submitCommand } from '../../../../sync/core/submitCommand';
import {
  patchWebCatalogStockInCache,
  patchWebCatalogStocksBatchInCache,
} from '../../../../catalog/infra/catalogRamSnapshotCache';
import {
  findStockInWebCache,
  getStockRowForAdjustment,
  listStocksForBulkCoefficient,
  type StockAdjustmentRow,
} from './stockAdjustmentsRead';

export type PhysicalCountMixedItem =
  | { kind: 'adjust'; stockId: string; delta: number; variantId?: string | null }
  | { kind: 'receive'; product: { id: string; name: string }; quantity: number; unitCost?: number };

export type AdjustStockParams = {
  stockId: string;
  delta: number;
  reason: string;
  performedByUserId?: string | null;
  originDeviceId?: string | null;
};

export const adjustStock = async (params: AdjustStockParams): Promise<string> => {
  const { stockId, delta, reason } = params;
  const stock = await getStockRowForAdjustment(stockId);
  if (!stock) {
    throw new StockProductNotFoundError('No encontramos el producto a ajustar.');
  }
  await assertProductIsInventoriable(stock.product_id);
  await assertNoParentStockOnVariantProduct(stock.product_id, stock.variant_id);

  const movementId = crypto.randomUUID();
        const performedAt = new Date().toISOString();
        const nextQuantity = stock.quantity + delta;
        const allowNegative = reasonAllowsNegativeStock(reason);
        if (nextQuantity < 0 && !allowNegative) {
          throw new StockInsufficientError('El ajuste dejaría stock negativo.');
        }

        // Guard #688: FEFO/lotes son a nivel producto y solo aplican a la fila
        // parent (ADR-016, F-10-fefo). Para celdas de variante no hay lotes.
        const skipFefo = stock.variant_id != null;

        // Ajuste positivo con lote: crear lote local + incluir lotPayload en el comando
        let lotPayload: Record<string, unknown> | undefined;
        if (delta > 0 && !skipFefo) {
          const lotId = await insertStockLot(
            {
              productId: stock.product_id,
              branchId: stock.branch_id,
              receivedAt: performedAt,
              expiresAt: null,
              unitCost: stock.avg_cost,
              quantity: delta,
              sourceType: 'manual_adjustment',
              sourceRefId: movementId,
            },
          );
          lotPayload = {
            operationId: crypto.randomUUID(),
            lotId,
            productId: stock.product_id,
            branchId: stock.branch_id,
            receivedAt: performedAt,
            expiresAt: null,
            unitCost: stock.avg_cost,
            initialQuantity: delta,
            remainingQuantity: delta,
            sourceType: 'manual_adjustment',
            sourceRefId: movementId,
          };
        }

        // Ajuste negativo con FEFO: planear + aplicar allocations local + incluir fefoAllocations en el comando.
        // #1497: consume lotes vencidos primero — si faltan unidades físicas, las
        // vencidas son las menos probables en góndola; además auto-cura divergencias.
        let fefoAllocations: Array<Record<string, unknown>> | undefined;
        if (delta < 0 && !skipFefo) {
          const absQty = Math.abs(delta);
          const allocations = await planFefoAllocations(stock.product_id, stock.branch_id, absQty, 'expiredFirst');
          if (allocations.length > 0) {
            const appliedAllocations = await applyFefoAllocations(allocations, 'adjustment', movementId, performedAt);
            fefoAllocations = appliedAllocations.map((alloc) => ({
              allocationId: alloc.allocationId,
              lotId: alloc.lotId,
              referenceType: alloc.referenceType,
              referenceId: alloc.referenceId,
              quantity: alloc.allocatedQuantity,
              createdAt: alloc.createdAt,
            }));
          }
        }

        const result = await submitCommand({
          entity: 'stock_adjustment',
          operation: 'insert',
          payload: {
            stockId,
            movementId,
            productId: stock.product_id,
            branchId: stock.branch_id,
            delta,
            reason,
            unitCost: stock.avg_cost,
            performedAt,
            performedByUserId: params.performedByUserId ?? null,
            originDeviceId: params.originDeviceId ?? null,
            variantId: stock.variant_id ?? null,
            ...(lotPayload ? { lotPayload } : {}),
            ...(fefoAllocations ? { fefoAllocations } : {}),
          },
        });
        if (!result.applied) {
          const error = new Error(
            result.ingestError?.trim()
              ? formatSyncErrorForUser(result.ingestError)
              : 'No se pudo ajustar el stock en el servidor. Reintentá.',
          );
          captureOpsFailure({
            module: 'inventory',
            op: 'stock.adjust',
            error,
            ingestError: result.ingestError ?? null,
            applied: result.applied,
            enqueued: result.enqueued,
            path: 'web-ram',
            itemId: stockId,
            extra: { stockId, productId: stock.product_id, branchId: stock.branch_id, variantId: stock.variant_id },
          });
          throw error;
        }
        patchWebCatalogStockInCache(stock.product_id, stock.branch_id, nextQuantity, undefined, undefined, stock.variant_id);
        void logBusinessAuditEvent('stock.adjust', {
          branchId: stock.branch_id,
          stockId,
          productId: stock.product_id,
          delta,
          reason,
          movementId,
        });
        return movementId;
};

export type ReceiveStockParams = {
  branchId: string;
  product: { id: string; name: string };
  quantity: number;
  unitCost: number;
  minQuantity: number;
  expiresAt?: string | null;
  lotNumber?: string | null;
  reason?: 'purchase_reception' | 'stock_restock' | 'stock_initial_load' | 'physical_count';
  performedByUserId?: string | null;
  originDeviceId?: string | null;
  supplierId?: string | null;
  referenceId?: string | null;
  variantId?: string | null;
};

/** Conteo físico atómico: ajustes sobre stock existente e ingresos iniciales para productos sin fila. */
export const applyPhysicalCountMixedBatch = async (
  branchId: string,
  items: PhysicalCountMixedItem[],
  opts?: { performedByUserId?: string | null; originDeviceId?: string | null }
): Promise<number> => {
  if (items.length === 0) return 0;

  const performedByUserId = opts?.performedByUserId ?? null;
  const originDeviceId = opts?.originDeviceId ?? null;

  const adjustItems = items.filter(
    (item): item is Extract<PhysicalCountMixedItem, { kind: 'adjust' }> => item.kind === 'adjust'
  );
  const receiveItems = items.filter(
    (item): item is Extract<PhysicalCountMixedItem, { kind: 'receive' }> => item.kind === 'receive'
  );

  const resolvedAdjustments: Array<{ stock: StockAdjustmentRow; delta: number }> = [];
  for (const item of adjustItems) {
    const stock = await getStockRowForAdjustment(item.stockId);
    if (!stock) {
      throw new StockProductNotFoundError('No encontramos el producto a ajustar.');
    }
    await assertProductIsInventoriable(stock.product_id);
    await assertNoParentStockOnVariantProduct(stock.product_id, stock.variant_id);
    resolvedAdjustments.push({ stock, delta: item.delta });
  }

  for (const item of receiveItems) {
    if (item.quantity <= 0) {
      throw new StockReceiveValidationError('La cantidad debe ser mayor a cero.');
    }
    await assertProductIsInventoriable(item.product.id);
  }

  // ADR-017: RPC compuesto atómico — reemplaza la transacción SQLite (no-op)
  // con un único submitCommand que aplica todo en una transacción PostgreSQL.
  const batchId = crypto.randomUUID();
  const rpcItems = items.map((item) => {
    if (item.kind === 'adjust') {
      return {
        kind: 'adjust',
        stockId: item.stockId,
        delta: item.delta,
        variantId: item.variantId ?? null,
      };
    }
    return {
      kind: 'receive',
      productId: item.product.id,
      quantity: item.quantity,
      unitCost: item.unitCost ?? 0,
    };
  });

  try {
    const result = await submitCommand({
      entity: 'physical_count_batch',
      operation: 'insert',
      payload: {
        batchId,
        branchId,
        deviceId: originDeviceId,
        performedByUserId,
        items: rpcItems,
      },
    });
    if (!result.applied) {
      const error = new Error(
        result.ingestError?.trim()
          ? formatSyncErrorForUser(result.ingestError)
          : 'No se pudo aplicar el conteo físico en el servidor. Reintentá.',
      );
      captureOpsFailure({
        module: 'inventory',
        op: 'stock.physical_count',
        error,
        ingestError: result.ingestError ?? null,
        applied: result.applied,
        enqueued: result.enqueued,
        path: 'web-ram',
        extra: { branchId, batchId },
      });
      throw error;
    }
  } catch (error: unknown) {
    captureOpsFailure({
      module: 'inventory',
      op: 'stock.physical_count',
      error,
      path: 'web-ram',
      extra: { branchId, batchId },
    });
    throw error;
  }

  // ADR-017: el listado lee del snapshot RAM (getStockByBranch), no de la DB.
  // El RPC es atómico, así que si llegamos acá todo el lote quedó aplicado —
  // patcheamos todas las celdas en un solo publish, igual que hacen
  // adjustStock/receiveStock, para que la grilla refleje el conteo sin reload.
  const cachePatches: Array<{
    productId: string;
    branchId: string;
    quantity: number;
    stockId?: string;
    avgCost?: number;
    variantId?: string | null;
  }> = [];
  for (const { stock, delta } of resolvedAdjustments) {
    cachePatches.push({
      productId: stock.product_id,
      branchId: stock.branch_id,
      quantity: stock.quantity + delta,
      variantId: stock.variant_id ?? null,
    });
  }
  for (const item of receiveItems) {
    const existing = findStockInWebCache(item.product.id, branchId, null);
    const unitCost = item.unitCost ?? 0;
    const nextQuantity = (existing?.quantity ?? 0) + item.quantity;
    // Réplica de inventory.apply_stock_adjustment_event: con delta>0 el costo
    // promedio es ponderado; en fila nueva queda avg_cost = unitCost.
    const nextAvgCost = existing
      ? nextQuantity > 0
        ? (existing.quantity * (existing.avg_cost ?? 0) + item.quantity * unitCost) / nextQuantity
        : existing.avg_cost
      : unitCost;
    cachePatches.push({
      productId: item.product.id,
      branchId,
      quantity: nextQuantity,
      stockId: existing?.id,
      avgCost: nextAvgCost,
      variantId: null,
    });
  }
  patchWebCatalogStocksBatchInCache(cachePatches);

  for (const { stock, delta } of resolvedAdjustments) {
    void logBusinessAuditEvent('stock.adjust', {
      branchId: stock.branch_id,
      stockId: stock.id,
      productId: stock.product_id,
      variantId: stock.variant_id ?? null,
      delta,
      reason: 'physical_count',
    });
  }
  for (const item of receiveItems) {
    void logBusinessAuditEvent('stock.receive', {
      branchId,
      productId: item.product.id,
      quantity: item.quantity,
      reason: 'stock_initial_load',
    });
  }

  return items.length;
};

export const receiveStock = async (params: ReceiveStockParams) => {
  const { product, quantity, unitCost, minQuantity, branchId, variantId } = params;
  if (quantity <= 0) {
    throw new StockReceiveValidationError('La cantidad debe ser mayor a cero.');
  }
  if (unitCost < 0) {
    throw new StockReceiveValidationError('El costo unitario no puede ser negativo.');
  }
  if (minQuantity < 0) {
    throw new StockReceiveValidationError('El stock mínimo no puede ser negativo.');
  }
  await assertProductIsInventoriable(product.id);

  const existing = findStockInWebCache(product.id, branchId, variantId);
        const movementId = crypto.randomUUID();
        const performedAt = new Date().toISOString();
        const stockId = existing?.id ?? crypto.randomUUID();
        const nextQuantity = (existing?.quantity ?? 0) + quantity;
        const nextAvgCost = existing
          ? ((existing.quantity * (existing.avg_cost ?? 0)) + quantity * unitCost) / nextQuantity
          : unitCost;
        const reason = params.reason ?? (existing ? 'stock_restock' : 'stock_initial_load');

        // Guard #688: en celdas de variante no se crean lotes (FEFO es a nivel
        // producto, ver ADR-016). El ingreso igual suma al stock de la celda.
        let lotPayload: Record<string, unknown> | undefined;
        if (variantId == null) {
          const normalizedExpiry = params.expiresAt?.trim() ? params.expiresAt.trim() : null;
          const lotId = await insertStockLot(
            {
              productId: product.id,
              branchId,
              receivedAt: performedAt,
              expiresAt: normalizedExpiry,
              unitCost,
              quantity,
              sourceType: reason,
              sourceRefId: movementId,
              lotNumber: params.lotNumber ?? null,
            },
          );
          lotPayload = {
            operationId: crypto.randomUUID(),
            lotId,
            productId: product.id,
            branchId,
            receivedAt: performedAt,
            expiresAt: normalizedExpiry,
            unitCost,
            initialQuantity: quantity,
            remainingQuantity: quantity,
            sourceType: reason,
            sourceRefId: movementId,
            lotNumber: params.lotNumber ?? null,
          };
        }

        const result = await submitCommand({
          entity: 'stock_adjustment',
          operation: 'insert',
          payload: {
            stockId,
            movementId,
            productId: product.id,
            branchId,
            delta: quantity,
            reason,
            unitCost: nextAvgCost,
            performedAt,
            performedByUserId: params.performedByUserId ?? null,
            originDeviceId: params.originDeviceId ?? null,
            supplierId: params.supplierId ?? null,
            referenceId: params.referenceId ?? null,
            minQuantity,
            expiresAt: params.expiresAt ?? null,
            lotNumber: params.lotNumber ?? null,
            variantId: variantId ?? null,
            ...(lotPayload ? { lotPayload } : {}),
          },
        });
        if (!result.applied) {
          const error = new Error(
            result.ingestError?.trim()
              ? formatSyncErrorForUser(result.ingestError)
              : 'No se pudo ingresar stock en el servidor. Reintentá.',
          );
          captureOpsFailure({
            module: 'inventory',
            op: 'stock.receive',
            error,
            ingestError: result.ingestError ?? null,
            applied: result.applied,
            enqueued: result.enqueued,
            path: 'web-ram',
            itemId: product.id,
            extra: { productId: product.id, branchId, stockId, variantId },
          });
          throw error;
        }
        patchWebCatalogStockInCache(product.id, branchId, nextQuantity, stockId, nextAvgCost, variantId);
        void logBusinessAuditEvent('stock.receive', {
          branchId,
          stockId,
          productId: product.id,
          quantity,
          reason,
          variantId: variantId ?? null,
        });
        return { stockId, movementId };
};

export const bulkUpdateQuantitiesByCoefficient = async (
  branchId: string,
  productIds: string[],
  coefficient: number,
  opts?: { performedByUserId?: string | null; originDeviceId?: string | null }
): Promise<number> => {
  const performedByUserId = opts?.performedByUserId ?? null;
  const originDeviceId = opts?.originDeviceId ?? null;
  if (productIds.length === 0) return 0;

  const rows = await listStocksForBulkCoefficient(branchId, productIds);

  const adjustments: Array<{ stock: StockAdjustmentRow; delta: number }> = [];
  for (const row of rows) {
    const rawNewQuantity = row.quantity * coefficient;
    const newQuantity = quantityAllowsFraction(row.unit_of_measure) ? rawNewQuantity : Math.round(rawNewQuantity);
    if (newQuantity === row.quantity) continue;
    adjustments.push({
      stock: {
        id: row.id,
        product_id: row.product_id,
        branch_id: branchId,
        quantity: row.quantity,
        avg_cost: row.avg_cost ?? 0,
      },
      delta: newQuantity - row.quantity,
    });
  }

  if (adjustments.length === 0) return 0;

  // #866: recolectar fallos en vez de throw en el primer fallo.
        // Si el producto K falla, los anteriores ya están aplicados —
        // procesamos todos y reportamos al final qué falló para que soporte pueda reparar.
        const failedProductIds: string[] = [];
        let appliedCount = 0;
        for (const { stock, delta } of adjustments) {
          try {
            await adjustStock({
              stockId: stock.id,
              delta,
              reason: 'bulk_coefficient',
              performedByUserId,
              originDeviceId,
            });
            appliedCount += 1;
          } catch (error: unknown) {
            failedProductIds.push(stock.product_id);
            captureOpsFailure({
              module: 'inventory',
              op: 'stock.bulk_coefficient',
              error,
              path: 'web-ram',
              itemId: stock.id,
              extra: { branchId, coefficient, productId: stock.product_id, delta },
            });
          }
        }
        if (failedProductIds.length > 0) {
          throw new Error(
            `Se aplicaron ${appliedCount} de ${adjustments.length} ajustes. Fallaron ${failedProductIds.length} productos. No reintentar el lote completo: avisá a soporte con estos IDs: ${failedProductIds.join(', ')}.`
          );
        }

  void logBusinessAuditEvent('stock.bulk_coefficient', {
    branchId,
    coefficient,
    affectedRows: adjustments.length,
  });

  return adjustments.length;
};
